import React, { useState } from 'react';
import api from '../../utils/axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';

const soloDigitos = (v) => (v || '').replace(/\D/g, '');

// Asignar un CVU sin identificar a un CUIT: lo agrega al padrón de ese cliente y pasa sus alertas en espera al caso.
export const AsignarCuentaDialog = ({ cuenta, onClose, onDone }) => {
  const [cuit, setCuit] = useState(cuenta?.motivo === 'CUIT_CONFLICTO' ? '' : soloDigitos(cuenta?.cuit_detectado));
  const [enviando, setEnviando] = useState(false);
  if (!cuenta) return null;
  const conflicto = cuenta.motivo === 'CUIT_CONFLICTO';

  const enviar = async () => {
    if (soloDigitos(cuit).length !== 11) return toast.error('El CUIT tiene que tener 11 dígitos');
    setEnviando(true);
    try {
      const { data } = await api.post(`/cuentas-sin-identificar/${cuenta.cvu}/asignar`, { cuit: soloDigitos(cuit) });
      const alertas = data.procesadas ? ` · ${data.procesadas} alerta(s) pasaron al caso del cliente` : '';
      toast.success(`CVU asignado al CUIT ${data.cuit}${alertas}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo asignar la cuenta');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ backgroundColor: '#ffffff' }} data-testid="csi-asignar-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Asignar a CUIT</DialogTitle>
          <DialogDescription className="text-slate-600">
            El CVU <span className="font-mono">{cuenta.cvu}</span> se agrega al padrón de ese cliente (si no existe se crea)
            y sus {cuenta.alertas_en_espera || 0} alerta(s) en espera pasan a su caso.
          </DialogDescription>
        </DialogHeader>
        {conflicto && (
          <div className="flex gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="csi-asignar-conflicto">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p>En el padrón este CVU es del CUIT <b className="font-mono">{cuenta.cuit_en_padron}</b>; el movimiento trajo
                <b className="font-mono"> {cuenta.cuit_detectado}</b>. Si elegís otro CUIT, el CVU sale del cliente anterior.</p>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setCuit(cuenta.cuit_en_padron)} data-testid="csi-usar-cuit-padron">
                  Usar el del padrón
                </Button>
                <Button size="sm" variant="outline" onClick={() => setCuit(soloDigitos(cuenta.cuit_detectado))} data-testid="csi-usar-cuit-detectado">
                  Usar el del movimiento
                </Button>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-slate-700">CUIT del cliente *</Label>
          <Input value={cuit} onChange={(e) => setCuit(e.target.value)} placeholder="20123456789" className="font-mono"
            style={{ backgroundColor: '#ffffff' }} data-testid="csi-asignar-cuit-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="csi-asignar-cancelar">Cancelar</Button>
          <Button onClick={enviar} disabled={enviando} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="csi-asignar-confirmar">
            {enviando ? 'Asignando…' : 'Asignar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DescartarCuentaDialog = ({ cuenta, onClose, onDone }) => {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  if (!cuenta) return null;

  const enviar = async () => {
    if (!motivo.trim()) return toast.error('El motivo es obligatorio');
    setEnviando(true);
    try {
      await api.post(`/cuentas-sin-identificar/${cuenta.cvu}/descartar`, { motivo: motivo.trim() });
      toast.success('Cuenta descartada');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo descartar la cuenta');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ backgroundColor: '#ffffff' }} data-testid="csi-descartar-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Descartar cuenta</DialogTitle>
          <DialogDescription className="text-slate-600">
            El CVU <span className="font-mono">{cuenta.cvu}</span> deja de estar pendiente y sus alertas en espera se descartan.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-slate-700">Motivo *</Label>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Por qué se descarta…"
            style={{ backgroundColor: '#ffffff' }} data-testid="csi-descartar-motivo" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="csi-descartar-cancelar">Cancelar</Button>
          <Button onClick={enviar} disabled={enviando} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="csi-descartar-confirmar">
            {enviando ? 'Descartando…' : 'Descartar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
